<script>
export default {
  name: 'WhatsAppCampaignCreatePage',
};
</script>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert, useTrack } from 'dashboard/composables';
import { CAMPAIGN_TYPES } from 'shared/constants/campaign.js';
import { CAMPAIGNS_EVENTS } from 'dashboard/helper/AnalyticsHelper/events.js';
import { useRoute, useRouter } from 'vue-router';

import Button from 'dashboard/components-next/button/Button.vue';
import WhatsAppCampaignForm from 'dashboard/components-next/Campaigns/Pages/CampaignPage/WhatsAppCampaign/WhatsAppCampaignForm.vue';

const store = useStore();
const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const currentCategory = computed(() => {
  const category = route.query.category?.toString().toLowerCase();
  return ['marketing', 'utility'].includes(category) ? category : 'utility';
});

const campaignTypeLabel = computed(() =>
  currentCategory.value === 'marketing'
    ? t('CAMPAIGN.WHATSAPP.TYPE_SELECTION.MARKETING.TITLE')
    : t('CAMPAIGN.WHATSAPP.TYPE_SELECTION.UTILITY.TITLE')
);

const campaignTypeTone = computed(() =>
  currentCategory.value === 'marketing'
    ? 'bg-n-amber-3 text-n-amber-11'
    : 'bg-n-teal-3 text-n-teal-11'
);

const handleClose = () => {
  const campaignCategory = route.query.category;
  if (['utility', 'marketing', 'all'].includes(campaignCategory)) {
    router.push({
      name: 'campaigns_whatsapp_category_index',
      params: { campaignCategory },
      query: route.query.inbox_id ? { inbox_id: route.query.inbox_id } : {},
    });
    return;
  }

  router.push({ name: 'campaigns_whatsapp_index' });
};

const addCampaign = async campaignDetails => {
  try {
    await store.dispatch('campaigns/create', campaignDetails);

    useTrack(CAMPAIGNS_EVENTS.CREATE_CAMPAIGN, {
      type: CAMPAIGN_TYPES.ONE_OFF,
    });

    useAlert(t('CAMPAIGN.WHATSAPP.CREATE.FORM.API.SUCCESS_MESSAGE'));
    handleClose();
  } catch (error) {
    const errorMessage =
      error?.response?.message ||
      t('CAMPAIGN.WHATSAPP.CREATE.FORM.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const handleSubmit = campaignDetails => {
  addCampaign(campaignDetails);
};
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <header
      class="sticky top-0 z-10 px-4 bg-white dark:bg-n-solid-2 border-b border-n-weak dark:border-n-strong shrink-0 sm:px-6"
    >
      <div class="w-full max-w-7xl mx-auto">
        <div
          class="flex flex-col justify-center w-full gap-3 py-4 sm:flex-row sm:items-center sm:justify-between"
        >
          <div class="flex items-center gap-3 min-w-0">
            <Button
              v-tooltip.top="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TOOLTIPS.BACK')"
              icon="i-lucide-arrow-left"
              variant="ghost"
              color="slate"
              size="sm"
              class="flex-shrink-0"
              @click="handleClose"
            />
            <div class="min-w-0">
              <span class="block text-heading-1 text-n-slate-12 truncate">
                {{ t('CAMPAIGN.WHATSAPP.CREATE.TITLE') }}
              </span>
              <span class="block mt-1 text-sm text-n-slate-11 truncate">
                {{ t('CAMPAIGN.WHATSAPP.CREATE.SUBTITLE') }}
              </span>
            </div>
          </div>
          <span
            class="inline-flex items-center self-start gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg sm:self-center"
            :class="campaignTypeTone"
          >
            <span class="i-lucide-badge-check size-3.5" />
            {{ campaignTypeLabel }}
          </span>
        </div>
      </div>
    </header>

    <main class="flex-1 px-4 overflow-y-auto py-6 sm:px-6">
      <div class="w-full max-w-7xl mx-auto">
        <WhatsAppCampaignForm @submit="handleSubmit" @cancel="handleClose" />
      </div>
    </main>
  </section>
</template>
